import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import Pagination from "components/common/pagination/Pagination";
import {
  TextCell,
  CheckboxCell,
  CircularProgressIndicator,
  StatusBadge,
  GenerateReportButton,
  DateCell,
  IntegrityTrafficLight, // ← NUEVO: Componente del semáforo
} from "./TableComponents";
import { ENHANCED_TABLE_COLUMNS, ITEMS_PER_PAGE } from "../utils/constants";

const CandidateList = ({
  paginatedCandidates,
  totalItems,
  filteredCandidatesCount,
  allCandidatesCount,
  currentPage,
  searchTerm,
  isGeneratingReport,
  onPageChange,
  onGenerateReport,
  onBulkDownload,
}) => {
  // Estado para manejar la selección múltiple
  const [selectedCandidates, setSelectedCandidates] = useState([]);

  // Manejar selección individual
  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates((prev) => {
      if (prev.includes(candidateId)) {
        return prev.filter((id) => id !== candidateId);
      }
      return [...prev, candidateId];
    });
  };

  // Manejar selección de todos
  const handleSelectAll = () => {
    if (selectedCandidates.length === paginatedCandidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(paginatedCandidates.map((c) => c.id_candidato));
    }
  };

  // Verificar si todos están seleccionados
  const allSelected =
    paginatedCandidates.length > 0 &&
    selectedCandidates.length === paginatedCandidates.length;

  // Manejar descarga masiva
  const handleBulkDownloadClick = () => {
    if (selectedCandidates.length === 0) {
      alert("Por favor, selecciona al menos un candidato");
      return;
    }
    onBulkDownload(selectedCandidates);
  };

  // Formatear datos para la tabla
  const { formattedRows, tableData } = useMemo(() => {
    const rows = paginatedCandidates.map((candidate, index) => {
      // TEMPORAL: Descomentar para ver qué datos vienen del backend
      // console.log("Datos del candidato:", candidate);
      
      // DATOS DE PRUEBA TEMPORALES - ELIMINAR CUANDO BACKEND ESTÉ LISTO
      // Esto simula diferentes índices de riesgo para ver los 4 colores del semáforo
      const indiceRiesgoPrueba = index % 4; // Rotará entre 0, 1, 2, 3
      // Descomenta la siguiente línea para ver todos en verde (0):
      // const indiceRiesgoPrueba = 0;
      // Descomenta para ver todos en amarillo (1):
      // const indiceRiesgoPrueba = 1;
      // Descomenta para ver todos en rojo (2 o 3):
      // const indiceRiesgoPrueba = 2;
      
      return {
        checkbox: (
          <CheckboxCell
            checked={selectedCandidates.includes(candidate.id_candidato)}
            onChange={() => handleSelectCandidate(candidate.id_candidato)}
          />
        ),
        nombre: <TextCell value={candidate.nombre_completo} />,
        documento: <TextCell value={candidate.documento} />,
        email: <TextCell value={candidate.email} />,
        edad: <TextCell value={candidate.edad} />,
        genero: <TextCell value={candidate.genero} />,
        cargo: <TextCell value={candidate.cargo_aplicado} />,
        empresa: <TextCell value={candidate.empresa || "N/A"} />,
        ajuste_cargo: (
          <CircularProgressIndicator
            percentage={candidate.ajuste_cargo_porcentaje || 0}
          />
        ),
        // SEMÁFORO DE INTEGRIDAD LABORAL
        // MODO PRUEBA: Usando datos temporales (indiceRiesgoPrueba)
        // PRODUCCIÓN: Cuando el backend esté listo, cambiar a:
        //    indiceRiesgo={candidate.indice_riesgo}
        //    (o el nombre del campo que confirme el backend)
        integridad_laboral: (
          <IntegrityTrafficLight 
            indiceRiesgo={indiceRiesgoPrueba}
            // indiceRiesgo={candidate.indice_riesgo} // ← Descomentar cuando backend esté listo
          />
        ),
        fecha: <DateCell date={candidate.fecha_aplicacion} />,
        acciones: (
          <GenerateReportButton
            candidateId={candidate.id_candidato}
            onGenerateReport={onGenerateReport}
            isLoading={isGeneratingReport}
          />
        ),
      };
    });

    return {
      formattedRows: rows,
      tableData: {
        columns: ENHANCED_TABLE_COLUMNS(allSelected, handleSelectAll),
        rows: rows,
      },
    };
  }, [
    paginatedCandidates,
    selectedCandidates,
    allSelected,
    onGenerateReport,
    isGeneratingReport,
  ]);

  return (
    <Grid container spacing={6}>
      <Grid item xs={12}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDBox>
              <MDTypography variant="h6" color="white">
                Generar Informe Psicológico - Seleccionar Candidato
              </MDTypography>
              <MDTypography variant="caption" color="white" display="block">
                ({totalItems} candidatos total
                {filteredCandidatesCount !== allCandidatesCount
                  ? `, ${filteredCandidatesCount} filtrados`
                  : ""}
                )
                {searchTerm && (
                  <MDTypography variant="caption" color="white" display="block">
                    Filtrado por: &quot;{searchTerm}&quot;
                  </MDTypography>
                )}
              </MDTypography>
              {selectedCandidates.length > 0 && (
                <MDTypography
                  variant="caption"
                  color="white"
                  display="block"
                  fontWeight="bold"
                >
                  {selectedCandidates.length} candidato(s) seleccionado(s)
                </MDTypography>
              )}
            </MDBox>

            {/* Botón de descarga masiva */}
            <MDButton
              variant="gradient"
              color="success"
              size="medium"
              onClick={handleBulkDownloadClick}
              disabled={selectedCandidates.length === 0}
            >
              <Icon>download</Icon>&nbsp;Descargar Reporte (
              {selectedCandidates.length})
            </MDButton>
          </MDBox>

          <MDBox pt={3}>
            <DataTable
              table={tableData}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
            />
          </MDBox>

          <MDBox pt={3}>
            <Pagination
              itemsPerPage={ITEMS_PER_PAGE}
              totalItems={totalItems}
              currentPage={currentPage}
              onPageChange={onPageChange}
            />
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );
};

CandidateList.propTypes = {
  paginatedCandidates: PropTypes.array.isRequired,
  totalItems: PropTypes.number.isRequired,
  filteredCandidatesCount: PropTypes.number.isRequired,
  allCandidatesCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  searchTerm: PropTypes.string,
  isGeneratingReport: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onGenerateReport: PropTypes.func.isRequired,
  onBulkDownload: PropTypes.func.isRequired,
};

export default CandidateList;